import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { adminApi, adminAuth, type CustomerSummary } from '../lib/adminApi';

const PAGE_SIZE = 20;

/** Customers list – one row per unique email, aggregated on the server. */
export function AdminCustomers() {
  const navigate = useNavigate();
  const [items, setItems] = useState<CustomerSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [archive, setArchive] = useState<'active' | 'archived' | 'all'>('active');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) return navigate('/admin/login');
  }, [navigate]);

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) return;
    setLoading(true);
    const handle = window.setTimeout(() => {
      adminApi
        .listCustomers({ q: q || undefined, page, pageSize: PAGE_SIZE, archive })
        .then((r) => {
          setItems(r.items);
          setTotal(r.total);
        })
        .catch((e) => setError(String(e.message)))
        .finally(() => setLoading(false));
    }, 200);
    return () => window.clearTimeout(handle);
  }, [q, page, archive]);

  return (
    <AdminLayout>
      <h1 className="text-2xl font-semibold text-slate-900">Customers</h1>
      <p className="mt-1 text-sm text-slate-500">
        Every unique email that's filed with us. Click a row to see the full record.
      </p>

      {error && <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Search by email, company name, or director…"
          className="input max-w-md flex-1"
        />
        <div className="inline-flex rounded-md bg-slate-100 p-1 text-xs">
          {(['active', 'archived', 'all'] as const).map((opt) => (
            <button
              key={opt}
              onClick={() => { setArchive(opt); setPage(1); }}
              className={`rounded px-3 py-1 capitalize ${
                archive === opt
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
        {loading && <span className="text-xs text-slate-400">Loading…</span>}
      </div>

      <div className="mt-4 overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3 text-right">Companies</th>
              <th className="px-4 py-3 text-right">Filings</th>
              <th className="px-4 py-3 text-right">Lifetime</th>
              <th className="px-4 py-3 text-right">Last filing</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((c) => (
              <tr key={c.email} className={`hover:bg-slate-50 ${c.archived ? 'opacity-60' : ''}`}>
                <td className="px-4 py-3">
                  <Link
                    to={`/admin/customers/${encodeURIComponent(c.email)}`}
                    className="block min-w-0"
                  >
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-slate-900 hover:text-brand-700">
                        {c.displayName || c.email}
                      </span>
                      {c.archived && (
                        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                          Archived
                        </span>
                      )}
                    </div>
                    <div className="truncate text-xs text-slate-500">{c.email}</div>
                  </Link>
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{c.companyCount}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {c.totalFilings}
                  {c.acceptedCount > 0 && (
                    <span className="ml-1 text-[10px] text-green-600">{c.acceptedCount}✓</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">£{(c.lifetimePence / 100).toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-xs text-slate-500">
                  {c.lastOrderAt ? new Date(c.lastOrderAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '–'}
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-500">
                  No customers match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-slate-500">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-ghost">
              ← Prev
            </button>
            <button onClick={() => setPage((p) => p + 1)} disabled={page * PAGE_SIZE >= total} className="btn-ghost">
              Next →
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
